import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store";
import { getGeoDropdownOptions } from "helpers/geoDropdown";
import styles from "../CmsFooterAppsEditModal.module.scss";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import { CmsFooterAppsSlot } from "../components/CmsFooterAppsSlot";

import {
  Application,
  APPLICATION_PLATFORM_KEYS,
  ApplicationPlatform,
  ApplicationSlotData,
} from "store/cms/footer/applications/slice";
import {
  handleUpdateDraftItem as onUpdateDraftItem,
  handleClearSlot as onClearSlot,
  handleUpdateDraftItemSlot as onUpdateDraftItemSlot,
} from "store/cms/footer/applications/slice";

type Props = {
  newUploadedImages: React.MutableRefObject<{
    [key in APPLICATION_PLATFORM_KEYS]?: File;
  }>;
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  readOnly?: boolean;
};

const AppsMainTab: React.FC<Props> = ({
  newUploadedImages,
  fieldErrors,
  clearFieldError,
  readOnly
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const draftItem = useAppSelector(
    (store) => store["cms-applications"].draftItem,
  );
  const geo = useAppSelector((store) => store["settings"].geoList);

  const handleUpdateDraftItem = <K extends keyof Application>(
    key: K,
    value: Application[K],
  ) => {
    dispatch(onUpdateDraftItem({ [key]: value }));
  };

  const handleClearSlot = (key: APPLICATION_PLATFORM_KEYS) => {
    newUploadedImages.current[key] = undefined;
    dispatch(onClearSlot(key));
  };

  type THandleUpdateSlot<
    T = Omit<ApplicationSlotData, "image"> & { image?: File },
    K extends keyof T = keyof T,
  > = {
    key: K;
    value: T[K];
    id: APPLICATION_PLATFORM_KEYS;
  };

  const handleUpdateDraftItemSlot = ({ key, value, id }: THandleUpdateSlot) => {
    if (key === "image" && value instanceof File) {
      newUploadedImages.current[id] = value;
      dispatch(onUpdateDraftItemSlot({ id, image: value }));
      return;
    }

    dispatch(onUpdateDraftItemSlot({ id, [key]: value }));
  };

  return (
    <>
      <div className={styles.cmsFooterPartnersContentUpper}>
        <InnerInput
          value={draftItem?.title ?? ""}
          placeholder={t("enterTitle")}
          label={t("title")}
          type="text"
          id="pages-title"
          onChange={(e) => {
            handleUpdateDraftItem("title", e);
            clearFieldError("title");
          }}
          hasError={Boolean(fieldErrors?.["title"])}
          disabled={readOnly}
        />

        <DefaultDropdown
          value={draftItem?.geo || "WW"}
          placeholder={t("geo")}
          label={t("geo")}
          options={getGeoDropdownOptions(geo)}
          onSelect={(v) => {
            handleUpdateDraftItem("geo", v as string);
            clearFieldError("geo");
          }}
          hasError={Boolean(fieldErrors?.["geo"])}
          disabled={readOnly}
        />

        <SwitchBtn
          activeTxt={t("active")}
          isActive={!!draftItem?.isActive}
          handleClick={() =>
            handleUpdateDraftItem("isActive", draftItem?.isActive === 0 ? 1 : 0)
          }
          disabled={readOnly}
        />
      </div>

      <div className={styles.cmsFooterPartnersContentLower}>
        {Object.keys(ApplicationPlatform).map((k) => {
          const key = k as APPLICATION_PLATFORM_KEYS;

          return (
            <CmsFooterAppsSlot
              key={key}
              id={key}
              name={ApplicationPlatform[key]}
              link={draftItem?.slots[key]?.href || ""}
              image={draftItem?.slots[key]?.imageHref || ""}
              isActive={!!draftItem?.isActive}
              onChangeLink={(v) =>
                handleUpdateDraftItemSlot({
                  key: "href",
                  value: v,
                  id: key,
                })
              }
              onAddImage={(v) =>
                handleUpdateDraftItemSlot({
                  key: "image",
                  value: v,
                  id: key,
                })
              }
              onClearSlot={() => handleClearSlot(key)}
              isModified={
                draftItem?.slots[key]?.isModified ||
                (!!draftItem?.slots[key]?.href &&
                  !!draftItem?.slots[key]?.imageHref)
              }
              readOnly={readOnly}
            />
          );
        })}
      </div>
    </>
  );
};

export default AppsMainTab;
